import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Select";
import { Badge } from "../../components/ui/Badge";
import { Table } from "../../components/ui/Table";
import { Modal } from "../../components/ui/Modal";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";

interface ModalidadRow {
  id: number;
  nombre: string;
  tipo: string;
  precio: number;
  orden: number;
  isVisible?: boolean;
  is_visible?: number | boolean;
  num_clientes?: number;
  createdAt?: string;
}

export function ModalidadesPage() {
  const [data, setData] = useState<ModalidadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [tipo, setTipo] = useState("todos");
  const [incluirInactivas, setIncluirInactivas] = useState(false);
  const [modalOpen, setModalOpen] = useState<"create" | "edit" | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState({ nombre: "", tipo: "diario", precio: "", orden: "1" });
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDesactivar, setConfirmDesactivar] = useState<ModalidadRow | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { incluir_inactivas: incluirInactivas ? "true" : "false" };
      if (tipo !== "todos") params.tipo = tipo;
      const res = await api.get<ModalidadRow[]>("/admin/modalidades", { params });
      setData(Array.isArray(res.data) ? res.data : []);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [tipo, incluirInactivas]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const primaNeta = form.precio ? (Number(form.precio) / 1.08195) : 0;
  const comision = primaNeta * 0.2;
  const gastosGestion = form.precio ? Number(form.precio) * 0.0045 : 0;

  const openCreate = () => {
    setForm({ nombre: "", tipo: "diario", precio: "", orden: "1" });
    setFormError("");
    setEditId(null);
    setModalOpen("create");
  };

  const openEdit = (row: ModalidadRow) => {
    setEditId(row.id);
    setForm({
      nombre: row.nombre,
      tipo: row.tipo,
      precio: String(row.precio),
      orden: String(row.orden ?? 1),
    });
    setFormError("");
    setModalOpen("edit");
  };

  const handleCreateSubmit = async () => {
    const nombre = form.nombre.trim();
    const precio = Number(form.precio);
    if (!nombre) { setFormError("Nombre obligatorio"); return; }
    if (Number.isNaN(precio) || precio <= 0) { setFormError("Precio debe ser mayor que 0"); return; }
    setSaving(true);
    setFormError("");
    try {
      await api.post("/admin/modalidades", {
        nombre,
        tipo: form.tipo,
        precio: parseFloat(String(form.precio)) || 0,
        orden: Number(form.orden) || 1,
      });
      setModalOpen(null);
      fetchList();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setFormError(err.response?.data?.error ?? "Error al crear");
    } finally {
      setSaving(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!editId) return;
    const nombre = form.nombre.trim();
    const precio = Number(form.precio);
    if (!nombre) { setFormError("Nombre obligatorio"); return; }
    if (Number.isNaN(precio) || precio <= 0) { setFormError("Precio debe ser mayor que 0"); return; }
    setSaving(true);
    setFormError("");
    try {
      await api.patch(`/admin/modalidades/${editId}`, {
        nombre,
        precio: parseFloat(String(form.precio)) || 0,
        orden: Number(form.orden) || 1,
      });
      setModalOpen(null);
      fetchList();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setFormError(err.response?.data?.error ?? "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleVisible = async (row: ModalidadRow) => {
    const visible = !(row.isVisible ?? (row.is_visible === 1 || row.is_visible === true));
    setTogglingId(row.id);
    try {
      await api.patch(`/admin/modalidades/${row.id}`, { is_visible: visible });
      fetchList();
    } finally {
      setTogglingId(null);
    }
  };

  const handleDesactivar = async () => {
    if (!confirmDesactivar) return;
    try {
      await api.delete(`/admin/modalidades/${confirmDesactivar.id}`);
      setConfirmDesactivar(null);
      fetchList();
    } catch {
      setConfirmDesactivar(null);
    }
  };

  const columns = [
    { key: "nombre", header: "Nombre", render: (r: ModalidadRow) => r.nombre },
    { key: "tipo", header: "Tipo", render: (r: ModalidadRow) => r.tipo === "anual" ? <Badge variant="success">Anual</Badge> : <Badge variant="info">Diario</Badge> },
    { key: "precio", header: "Precio", render: (r: ModalidadRow) => `${Number(r.precio).toFixed(2)} €` },
    { key: "orden", header: "Orden", render: (r: ModalidadRow) => r.orden ?? 1 },
    {
      key: "visible",
      header: "Visible",
      render: (r: ModalidadRow) => {
        const visible = r.isVisible ?? (r.is_visible === 1 || r.is_visible === true);
        return (
          <button
            type="button"
            onClick={() => handleToggleVisible(r)}
            disabled={togglingId === r.id}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${visible ? "bg-[#1A3AB8]" : "bg-gray-200"}`}
          >
            <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${visible ? "translate-x-5" : "translate-x-1"}`} />
          </button>
        );
      },
    },
    { key: "num_clientes", header: "Nº clientes", render: (r: ModalidadRow) => r.num_clientes ?? 0 },
    {
      key: "actions",
      header: "Acciones",
      render: (r: ModalidadRow) => (
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => openEdit(r)}>Editar</Button>
          <Button size="sm" variant="danger" onClick={() => setConfirmDesactivar(r)}>Desactivar</Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold text-[#2C2C2C]">Modalidades</h1>
        <Button onClick={openCreate}>Nueva modalidad</Button>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <Select
          options={[
            { value: "todos", label: "Todos" },
            { value: "diario", label: "Diario" },
            { value: "anual", label: "Anual" },
          ]}
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
          className="w-40"
        />
        <label className="flex items-center gap-2 text-sm text-[#2C2C2C]">
          <input type="checkbox" checked={incluirInactivas} onChange={(e) => setIncluirInactivas(e.target.checked)} />
          Mostrar inactivas
        </label>
      </div>

      <Table<ModalidadRow> columns={columns} data={data} loading={loading} emptyMessage="No hay modalidades" />

      <Modal isOpen={modalOpen === "create"} onClose={() => setModalOpen(null)} title="Nueva modalidad" footer={
        <>
          <Button variant="secondary" onClick={() => setModalOpen(null)}>Cancelar</Button>
          <Button loading={saving} onClick={handleCreateSubmit}>Crear</Button>
        </>
      }>
        {formError && <p className="mb-4 text-sm text-red-600">{formError}</p>}
        <div className="space-y-4">
          <Input label="Nombre" value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} required />
          <Select label="Tipo" options={[{ value: "diario", label: "Diario" }, { value: "anual", label: "Anual" }]} value={form.tipo} onChange={(e) => setForm((f) => ({ ...f, tipo: e.target.value }))} />
          <Input label="Precio (€)" type="number" step="0.01" min="0" value={form.precio} onChange={(e) => setForm((f) => ({ ...f, precio: e.target.value }))} required />
          <Input label="Orden" type="number" min="0" value={form.orden} onChange={(e) => setForm((f) => ({ ...f, orden: e.target.value }))} />
          <div className="rounded-lg bg-gray-50 p-4 text-sm">
            <p className="font-medium text-[#2C2C2C]">Desglose (orientativo)</p>
            <p>
              Prima neta (÷ 1,08195): {primaNeta.toFixed(2)} €
            </p>
            <span className="text-xs text-gray-400 block mb-2">
              IPS 8% + Consorcio 0,15% + Recargo 0,045% — Total impuestos 8,195%
            </span>
            <p>Comisión Circuit Cover (20%): {comision.toFixed(2)} €</p>
            <p>Gastos gestión TPV (0,45%): {gastosGestion.toFixed(2)} €</p>
          </div>
        </div>
      </Modal>

      <Modal isOpen={modalOpen === "edit"} onClose={() => setModalOpen(null)} title="Editar modalidad" footer={
        <>
          <Button variant="secondary" onClick={() => setModalOpen(null)}>Cancelar</Button>
          <Button loading={saving} onClick={handleEditSubmit}>Guardar</Button>
        </>
      }>
        {formError && <p className="mb-4 text-sm text-red-600">{formError}</p>}
        <div className="space-y-4">
          <Input label="Nombre" value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} required />
          <Input label="Tipo" value={form.tipo} disabled className="bg-gray-100" />
          <Input label="Precio (€)" type="number" step="0.01" min="0" value={form.precio} onChange={(e) => setForm((f) => ({ ...f, precio: e.target.value }))} required />
          <Input label="Orden" type="number" min="0" value={form.orden} onChange={(e) => setForm((f) => ({ ...f, orden: e.target.value }))} />
          <div className="rounded-lg bg-gray-50 p-4 text-sm">
            <p className="font-medium text-[#2C2C2C]">Desglose (orientativo)</p>
            <p>
              Prima neta (÷ 1,08195): {primaNeta.toFixed(2)} €
            </p>
            <span className="text-xs text-gray-400 block mb-2">
              IPS 8% + Consorcio 0,15% + Recargo 0,045% — Total impuestos 8,195%
            </span>
            <p>Comisión Circuit Cover (20%): {comision.toFixed(2)} €</p>
            <p>Gastos gestión TPV (0,45%): {gastosGestion.toFixed(2)} €</p>
          </div>
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!confirmDesactivar} onClose={() => setConfirmDesactivar(null)} onConfirm={handleDesactivar} title="Desactivar modalidad" message={confirmDesactivar ? `¿Desactivar "${confirmDesactivar.nombre}"?` : ""} confirmLabel="Desactivar" variant="danger" />
    </div>
  );
}
